import { IUser } from "redwood-model/dist/IUser"
import { logDebug, logError, logInfo } from "./log"
import { QS_REDIRECT_URI, REDIRECT_URL_ON_STARTUP, qs } from "./main"
import { PARAMS } from "./app/PARAMS"
import { AUTH_EVENTS, getSession } from "./app/amplify/amplify"
import { graphqlRequest, IrequestData } from "./app/graphqlHelper"
import { tryGetTokenFromAmplifyState } from "./app/amplify/amplify_state"
import { IPublication } from "redwood-model/dist"

const graphqlUrl = () => { return PARAMS["REDWOOD-LAMBDA"] + "/query"}

export const REDWOOD_ACTIVATION_URL = () => `${PARAMS["REDWOOD_UI"]}/school/overview`

export const setNeedsActivationInRedwood = (payloadOrSession: any) => {
    if (payloadOrSession.event ===  AUTH_EVENTS.confirmSignUp ||
        payloadOrSession?.needs_activation ||
        payloadOrSession.user?.needs_activation) { // new users should be redirected to redwood for action
        logInfo("needs activation handled")
        localStorage[REDIRECT_URL_ON_STARTUP] = REDWOOD_ACTIVATION_URL()
    }
}

export const redirect2RedwoodForActivation = () => {
  sessionStorage.setItem('needsActivation', '1')
  if (location.pathname !== '/link-school') {
    location.href = '/link-school';
  }
}

export const fetchUserById = async (id: number): Promise<IUser> => {
    try {
        const query = `query userByIdQuery($id: Int!) {
          userByIdQuery(id: $id) {
            id
            username
            first_name
            last_name
            email
            enabled
            uid
            needs_activation
            roles
            publications {
              id
              name
              slug
            }
          }
        }`

        const token = tryGetTokenFromAmplifyState(localStorage)
        const request: IrequestData = {
          url: graphqlUrl(),
          query,
          token,
          variables: {
            id
          }
        }
        // includeDisabled: true
        const { data } = await graphqlRequest(request)
        return data?.data["userByIdQuery"] as IUser
    } catch(e) {
      logError(e)

      return Promise.reject(e)
    }
}

export const fetchUserPublications = async (explicitUserId: number): Promise<IPublication[]> => {
  try {
    const token = tryGetTokenFromAmplifyState(localStorage)
    const variables = { user_id: explicitUserId };
    
    const publicationOperation = 'userPublicationsQuery'
    const licenceOperation = 'licenceSearchQuery'

    const publicationQuery = `
    query userPublicationsQuery($principal: String, $user_id: Int) {
          userPublicationsQuery(principal: $principal, user_id: $user_id) {
            id
            name
            slug
          }
      }`
    const licenceQuery = `query licenceSearchQuery($user_id: Int) {
          licenceSearchQuery( user_id: $user_id) {
            id
            publication
            valid_at,
            expired_at
          }
      }`

    const [publicationResult, licenceResult] = await Promise.all([
      graphqlRequest( {
        url: graphqlUrl(),
        query: publicationQuery,
        operationName: publicationOperation,
        variables,
        token
      }),
      graphqlRequest( {
        url: graphqlUrl(),
        query: licenceQuery,
        operationName: licenceOperation,
        variables: {...variables, take: 20, from: 0},
        token
      })
    ])

    const {data: licenceData, errors: licenceErrors} = licenceResult
    const {data: publicationData, errors: publicationErrors} = publicationResult

    if (publicationErrors?.length) {
      throw new Error(publicationErrors.map((err: any) => err.message).join(', '));
    }

    if (licenceErrors?.length) {
      throw new Error(licenceErrors.map((err: any) => err.message).join(', '));
    }

    const publications = publicationData?.data?.userPublicationsQuery
    const licences: {id: number, publication: string, valid_at: string, expired_at: string}[] = licenceData?.data?.licenceSearchQuery || []


    return licences.map((licence) => {
     const publication: IPublication = publications.find((p: IPublication) => p.slug === licence.publication) || {
       id: undefined,
       name: licence.publication,
       slug: licence.publication,
     }

      return {...publication,
        publication_licenses_id: licence.id,
        publication_licenses_expired_at: licence.expired_at
      }
    })
  } catch (err) {
    console.error(err)

    return Promise.reject(err)
  }
}

export const verifyEmailCode = async(email: string, code: string): Promise<boolean> => {
    try {
        const query = `mutation VerifyEmailCode ($email: String!, $code: Int!) {
          verifyEmailCode(input: {
            email: $email
            code: $code
          })
        }`

        const token = tryGetTokenFromAmplifyState(localStorage)
        const request: IrequestData = {
          url: graphqlUrl(),
          query,
          token,
          variables: {
            email,
            code
          }
        }

        const { data } = await graphqlRequest(request)

        return data?.data["verifyEmailCode"] as boolean
    } catch(e) {
      logError(e)

      return Promise.reject(e)
    }
}

export const expireRedwoodSessionCookie = (name: string = "REDWOODSESSID", value: string = ""): void  => {
  const domain = location.hostname.split(".").slice(-2).join(".")
  let cookieString = `${name}=${value};domain=${domain};path=/;Expires=${new Date(0).toUTCString()}`
  document.cookie = cookieString
  logDebug(`Setting cookie by name: ${name}, with value:`, value)
}

export const handleRedwoodSignupRedirect = () => {
  if (qs.has(QS_REDIRECT_URI)) {
    let redirect_uri = qs.get(QS_REDIRECT_URI)?.toLowerCase()
    if (redirect_uri?.endsWith("/")) {
      redirect_uri = redirect_uri.slice(0, -1)
    }
    logDebug(`redirect_uri ${redirect_uri},  ${PARAMS["REDWOOD_UI"]}/register`)
    if (redirect_uri === `${PARAMS["REDWOOD_UI"]}/register`) {
      location.href = `${location.origin}/signup`
    }
  }
}

export const fetchUserByPrincipal = async (principal: string): Promise<string|undefined> => {
    let email = undefined

    try {
      const session = await getSession()
      const token = tryGetTokenFromAmplifyState(localStorage)

      const query = `
      query userByPrincipalQuery($principal: String!) {
            userByPrincipalQuery(principal: $principal) {
                email
            }
        }`
      const variables = { principal: principal };

      const operationName = 'userByPrincipalQuery'

      const {data, errors} = await graphqlRequest( {
        url: graphqlUrl(),
        query,
        operationName,
        token,
        variables
      })

      return data?.data?.userByPrincipalQuery

    } catch (err) {
      logError(err)
    }
    finally {
      return email
    }
  }

export const sendVerifyEmail = async (email: string, salutation: string): Promise<boolean> => {
  const query = `mutation SendVerifyEmail($email: String!, $salutation: String!) {
    sendVerifyEmail(input: {
      email: $email
      salutation: $salutation
    })
  }`

  const token = tryGetTokenFromAmplifyState(localStorage)
  const request: IrequestData = {
    url: graphqlUrl(),
    query,
    token,
    variables: {
      email,
      salutation
    }
  }

  const { data } = await graphqlRequest(request)

  return data?.data["sendVerifyEmail"] as boolean
}

export const doubleCheckNeedsActivation = async (principal: string) => {
    try {
      const session = await getSession()
      const token = tryGetTokenFromAmplifyState(localStorage)

      const query = `query userByPrincipalQuery($principal: String!) {
        userByPrincipalQuery(principal: $principal) {
            id
            needs_activation
        }
      }`
      const variables = { principal: principal };

      const operationName = 'userByPrincipalQuery'

      const {data, errors} = await graphqlRequest( {
        url: graphqlUrl(),
        query,
        operationName,
        token,
        variables
      })

      return data?.data?.userByPrincipalQuery?.needs_activation

    } catch (err) {
      logError(err)
      throw err
    }
}
